import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { pageSEO } from '../utils/seo';

const ReservationSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reservationNumber, setReservationNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [error, setError] = useState('');
  const seo = pageSEO.checkReservation;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formatReservationNumber = (value: string) => {
    // 숫자, 영문자, 하이픈만 허용
    const cleaned = value.replace(/[^\dA-Za-z-]/g, '');

    // 최대 길이 제한 (하이픈 포함 15자: YYYYMMDD-A0001)
    return cleaned.slice(0, 15);
  };

  const handleReservationNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatReservationNumber(e.target.value);
    setReservationNumber(formatted);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!reservationNumber.trim() || !customerName.trim()) {
      setError(t('reservationSearch.missingFields'));
      return;
    }

    // 예약번호:이름 형태로 토큰 생성 (Base64 인코딩 - UTF-8 지원)
    const tokenData = `${reservationNumber.trim()}:${customerName.trim()}`;
    const token = btoa(unescape(encodeURIComponent(tokenData)));

    // 토큰으로 예약 조회 페이지로 이동
    navigate(`/reservation/detail?token=${encodeURIComponent(token)}`);
  };

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://goodtimerent.kr/search" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <Header />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-center mb-8">{t('reservationSearch.title')}</h1>

          {/* Search Form */}
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">{t('reservationSearch.searchTitle')}</h2>
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <form onSubmit={handleSearch}>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('reservationSearch.reservationNumber')}
                  </label>
                  <input
                    type="text"
                    value={reservationNumber}
                    onChange={handleReservationNumberChange}
                    placeholder={t('reservationSearch.reservationNumberPlaceholder')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('reservationSearch.customerName')}
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder={t('reservationSearch.customerNamePlaceholder')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                {t('reservationSearch.submit')}
              </button>
            </form>
          </div>
          </div>
        </div>
      </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default ReservationSearchPage;
